import { CheckIcon } from '@heroicons/react/24/solid'
import { useListCopy } from 'app/hooks/useListCopy'
import type { ListCopy } from 'lib/listCopy'
import { Modal } from './Modal'

interface UpgradeModalProps {
  open: boolean
  onClose: () => void
}

const CALENDLY_URL = `https://calendly.com/trackable/upgrade-to-crm`

const AI_AGENT_FEATURES = [
  `AI Voice Agent`,
  `Voice Agent Analytics`,
  `Limited Contacts`,
  `Limited Activities`,
  `Limited Call Intelligence`,
]

const crmFeatures = (copy: ListCopy) => [
  `Dynamic Lead Management`,
  `Full PMS data`,
  `Tasks`,
  copy.manualListsPermission,
  `Outbound calling`,
  `Call Intelligence`,
  `Marketing Analytics`,
  `Attribution Analytics`,
  `New Patient Analytics`,
]

function FeatureItem({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-4 w-4 items-center justify-center rounded-full">
        <CheckIcon className="h-3 w-3 text-green-600" />
      </div>
      <span className="text-xs font-medium text-strong">{text}</span>
    </div>
  )
}

export function UpgradeModal({ open, onClose }: UpgradeModalProps) {
  const copy = useListCopy()
  const crmFeaturesList = crmFeatures(copy)
  return (
    <Modal open={open} onClose={onClose} title="Upgrade plan" isFullScreen={false}>
      <div className="flex w-screen gap-4 md:w-[850px]">
        {/* AI Agent Column */}
        <div className="flex flex-1 flex-col gap-2.5 rounded-lg border border-default p-4 shadow-sm">
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-semibold text-strong">AI Agent</h3>
            <p className="text-xs text-subtle">
              See agent performance and review agent calls in the Trackable ecosystem
            </p>
          </div>
          <div className="flex flex-1 flex-col gap-2.5 pt-2">
            {AI_AGENT_FEATURES.map((feature) => (
              <FeatureItem key={feature} text={feature} />
            ))}
          </div>
          <button
            disabled
            className="mt-auto h-8 w-full rounded-md border border-default bg-transparent px-4 text-xs font-medium text-subtle"
          >
            Current Plan
          </button>
        </div>

        {/* CRM Column */}
        <div className="flex flex-1 flex-col gap-2.5 rounded-lg border border-default p-4 shadow-sm">
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-semibold text-strong">CRM</h3>
            <p className="text-xs text-subtle">
              Capture and track every lead. Analyze all of your phone calls. Unlock powerful marketing and attribution
              dashboards.
            </p>
          </div>
          <div className="flex flex-1 flex-col gap-2.5 pt-2">
            {crmFeaturesList.map((feature) => (
              <FeatureItem key={feature} text={feature} />
            ))}
          </div>
          <button
            onClick={() => window.open(CALENDLY_URL, `_blank`)}
            className="mt-auto h-8 w-full rounded-md bg-blue-600 px-4 text-xs font-medium text-on-color hover:bg-blue-700"
          >
            Talk to Sales
          </button>
        </div>
      </div>
    </Modal>
  )
}
